// 訊息管理頁面 - 聊天泡泡式介面（依 LINE ID 分組成對話串）
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';

export default function NotificationAdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const [manualRegisterOpen, setManualRegisterOpen] = useState(false);
  const [manualRegisterData, setManualRegisterData] = useState({
    lineId: '', productId: '', name: '', mobile: '', community: '', department: '', position: '', other: ''
  });
  const [userOptions, setUserOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/notifications');
      const data = await res.json();
      setNotifications(data);
    } catch (error) {
      setSnackbar({ open: true, message: '載入訊息失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  useEffect(() => {
    fetch('/api/users/line-list')
      .then(res => res.json())
      .then(data => {
        setUserOptions(data.map(u => ({ value: u.lineId, label: `${u.name || ''} (${u.lineId})`, userId: u.id })));
      });
    fetch('/api/products')
      .then(res => res.json())
      .then(data => setProducts(data.filter(p => p.stock > 0)));
  }, []);

  // 依 LINE ID 把訊息分組成一則則對話（沒有 lineId 的用 userId 分組，兩者都沒有就各自獨立一則）
  const conversations = useMemo(() => {
    const map = new Map();
    notifications.forEach((n) => {
      const key = n.lineId || (n.userId ? `u${n.userId}` : `n${n.id}`);
      if (!map.has(key)) {
        map.set(key, {
          key,
          lineId: n.lineId,
          userId: n.userId,
          name: n.user?.realName || n.user?.displayName || n.user?.name || n.lineId || '未知使用者',
          orgLabel: [n.user?.department, n.user?.position].filter(Boolean).join('／'),
          pictureUrl: n.user?.pictureUrl,
          messages: []
        });
      }
      map.get(key).messages.push(n);
    });
    const list = Array.from(map.values()).map((c) => {
      c.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      c.lastMessage = c.messages[c.messages.length - 1];
      return c;
    });
    list.sort((a, b) => new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt));
    return list;
  }, [notifications]);

  const filteredConversations = useMemo(() => {
    if (!searchText.trim()) return conversations;
    const q = searchText.toLowerCase();
    return conversations.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.lineId || '').toLowerCase().includes(q) ||
      c.messages.some((m) => (m.message || '').toLowerCase().includes(q))
    );
  }, [conversations, searchText]);

  const selectedConversation = conversations.find((c) => c.key === selectedKey) || null;

  // 送出回覆（都是走既有的 LINE 通知建立流程，會真正觸發推播/儲存記錄）
  const handleSendReply = async () => {
    if (!selectedConversation || !replyText.trim()) return;
    if (!selectedConversation.lineId) {
      setSnackbar({ open: true, message: '這則對話沒有 LINE ID，無法回覆', severity: 'warning' });
      return;
    }
    setSending(true);
    try {
      const res = await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selectedConversation.userId || undefined,
          lineId: selectedConversation.lineId,
          channel: 'LINE',
          message: replyText.trim()
        })
      });
      if (res.ok) {
        setReplyText('');
        fetchNotifications();
      } else {
        setSnackbar({ open: true, message: '傳送失敗', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '傳送失敗', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/notifications/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
        fetchNotifications();
      } else {
        setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
    setDeleteDialog({ open: false, id: null });
  };

  const handleResendTicket = async () => {
    if (!selectedConversation?.userId) return;
    try {
      const orderRes = await fetch(`/api/orders/user/${selectedConversation.userId}`, { credentials: 'include' });
      if (!orderRes.ok) {
        setSnackbar({ open: true, message: '找不到該用戶的訂單', severity: 'error' });
        return;
      }
      const order = await orderRes.json();
      const response = await fetch(`/api/orders/${order.id}/resend-ticket`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: `已成功重新發送電子票給 ${selectedConversation.name}`, severity: 'success' });
        fetchNotifications();
      } else {
        setSnackbar({ open: true, message: `重新發送失敗: ${result.error}`, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '系統錯誤，請稍後再試', severity: 'error' });
    }
  };

  const handleManualRegister = async () => {
    try {
      const { lineId, productId, ...formDataExtra } = manualRegisterData;
      if (!lineId || !productId) {
        setSnackbar({ open: true, message: '請選擇用戶和活動', severity: 'warning' });
        return;
      }
      const user = userOptions.find(u => u.value === lineId);
      if (!user) {
        setSnackbar({ open: true, message: '找不到指定的用戶', severity: 'error' });
        return;
      }
      const checkRes = await fetch(`/api/orders/check?productId=${productId}&lineId=${lineId}`);
      const checkData = await checkRes.json();
      if (checkData.registered) {
        setSnackbar({ open: true, message: '此用戶已經報名過此活動了！', severity: 'warning' });
        return;
      }
      const registerRes = await fetch('/api/admin/manual-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lineId,
          productId: parseInt(productId),
          extra: {
            社團: formDataExtra.community || '手動報名',
            社別: formDataExtra.department || '一般民眾',
            社職: formDataExtra.position || '一般民眾',
            姓名: formDataExtra.name || user.label.split(' (')[0],
            手機: formDataExtra.mobile || '',
            其它: formDataExtra.other || '管理員手動報名'
          }
        })
      });
      const result = await registerRes.json();
      if (registerRes.ok && result.success) {
        setSnackbar({ open: true, message: '手動報名成功！已發送 QR Code 給用戶', severity: 'success' });
        setManualRegisterOpen(false);
        setManualRegisterData({ lineId: '', productId: '', name: '', mobile: '', community: '', department: '', position: '', other: '' });
        fetchNotifications();
      } else {
        setSnackbar({ open: true, message: `手動報名失敗: ${result.error || '未知錯誤'}`, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '手動報名失敗，請稍後再試', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const formatTime = (t) => t ? new Date(t).toLocaleString('zh-TW') : '';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const showList = !isMobile || !selectedKey;
  const showThread = !isMobile || !!selectedKey;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          訊息管理
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PersonAddIcon />}
          onClick={() => setManualRegisterOpen(true)}
          size={isMobile ? 'small' : 'medium'}
        >
          手動報名
        </Button>
      </Box>

      <Paper elevation={1} sx={{ display: 'flex', height: '75vh', overflow: 'hidden' }}>
        {/* 左側：對話清單 */}
        {showList && (
          <Box sx={{ width: isMobile ? '100%' : 320, flexShrink: 0, borderRight: isMobile ? 'none' : '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="搜尋姓名、LINE ID 或訊息內容..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{ endAdornment: <SearchIcon fontSize="small" color="disabled" /> }}
              />
            </Box>
            <Divider />
            <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
              {filteredConversations.map((c) => (
                <ListItemButton
                  key={c.key}
                  selected={c.key === selectedKey}
                  onClick={() => setSelectedKey(c.key)}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <ListItemAvatar>
                    <Avatar src={c.pictureUrl}>{(c.name || '?').charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c.orgLabel ? `${c.name}・${c.orgLabel}` : c.name}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                          {c.lastMessage.status === 'RECEIVE' ? '' : '你：'}{c.lastMessage.message}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.disabled">
                          {formatTime(c.lastMessage.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
              {filteredConversations.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">暫無符合的對話</Typography>
                </Box>
              )}
            </List>
          </Box>
        )}

        {/* 右側：訊息泡泡串 */}
        {showThread && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedConversation ? (
              <>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isMobile && (
                    <IconButton size="small" onClick={() => setSelectedKey(null)}>
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Avatar src={selectedConversation.pictureUrl} sx={{ width: 32, height: 32 }}>
                    {(selectedConversation.name || '?').charAt(0)}
                  </Avatar>
                  <Typography noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedConversation.orgLabel ? `${selectedConversation.name}・${selectedConversation.orgLabel}` : selectedConversation.name}
                  </Typography>
                  {selectedConversation.userId && (
                    <Tooltip title="重新發送電子票">
                      <IconButton size="small" color="secondary" onClick={handleResendTicket}>
                        <TicketIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#f5f6f7' }}>
                  {selectedConversation.messages.map((m) => {
                    const incoming = m.status === 'RECEIVE';
                    return (
                      <Box
                        key={m.id}
                        sx={{
                          display: 'flex',
                          justifyContent: incoming ? 'flex-start' : 'flex-end',
                          '&:hover .msg-delete': { opacity: 1 }
                        }}
                      >
                        {!incoming && (
                          <IconButton
                            className="msg-delete"
                            size="small"
                            sx={{ opacity: 0, transition: 'opacity 0.15s', mr: 0.5, alignSelf: 'center' }}
                            onClick={() => setDeleteDialog({ open: true, id: m.id })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Box
                          sx={{
                            maxWidth: '70%',
                            minWidth: 0,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: incoming ? '#ffffff' : (m.status === 'FAILED' ? '#ffcdd2' : '#06C755'),
                            color: incoming ? 'text.primary' : (m.status === 'FAILED' ? 'text.primary' : '#ffffff'),
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {m.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: incoming ? 'left' : 'right' }}
                          >
                            {formatTime(m.createdAt)}{m.status === 'FAILED' ? '・發送失敗' : ''}{m.status === 'PENDING' ? '・待發送' : ''}
                          </Typography>
                        </Box>
                        {incoming && (
                          <IconButton
                            className="msg-delete"
                            size="small"
                            sx={{ opacity: 0, transition: 'opacity 0.15s', ml: 0.5, alignSelf: 'center' }}
                            onClick={() => setDeleteDialog({ open: true, id: m.id })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ p: 1.5, borderTop: '1px solid #eee', display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="輸入訊息回覆..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                    multiline
                    maxRows={4}
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSendReply}
                    disabled={sending || !replyText.trim()}
                  >
                    傳送
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">請從左側選擇一則對話</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* 手動報名對話框 */}
      <Dialog open={manualRegisterOpen} onClose={() => setManualRegisterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>手動報名</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>LINE ID</InputLabel>
                <Select
                  value={manualRegisterData.lineId}
                  label="LINE ID"
                  onChange={(e) => setManualRegisterData({ ...manualRegisterData, lineId: e.target.value })}
                >
                  {userOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>活動ID</InputLabel>
                <Select
                  value={manualRegisterData.productId}
                  label="活動ID"
                  onChange={(e) => setManualRegisterData({ ...manualRegisterData, productId: e.target.value })}
                >
                  {products.map(p => (
                    <MenuItem key={p.id} value={p.id}>{`${p.name} (ID: ${p.id})`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="姓名" placeholder="可留空，將使用用戶名稱" value={manualRegisterData.name} onChange={(e) => setManualRegisterData({ ...manualRegisterData, name: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="手機" placeholder="可留空" value={manualRegisterData.mobile} onChange={(e) => setManualRegisterData({ ...manualRegisterData, mobile: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="社團" placeholder="可留空" value={manualRegisterData.community} onChange={(e) => setManualRegisterData({ ...manualRegisterData, community: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="社別" placeholder="可留空" value={manualRegisterData.department} onChange={(e) => setManualRegisterData({ ...manualRegisterData, department: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="社職" placeholder="可留空" value={manualRegisterData.position} onChange={(e) => setManualRegisterData({ ...manualRegisterData, position: e.target.value })} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="其它" placeholder="可留空" value={manualRegisterData.other} onChange={(e) => setManualRegisterData({ ...manualRegisterData, other: e.target.value })} variant="outlined" size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualRegisterOpen(false)}>取消</Button>
          <Button onClick={handleManualRegister} variant="contained">執行報名</Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除這則訊息嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>取消</Button>
          <Button onClick={() => handleDelete(deleteDialog.id)} color="error" variant="contained">刪除</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
